// @ts-strict-ignore
import type { Promotion } from '@empathybroker/tsclient-play/dist/models/promotion'
import type { FeatureIdEnum } from '../../enums'
import type { ToolReportEntry, ToolReportingListParams } from '../../models'
import type { ToolReportingService } from '../../services/interfaces'
import type { ToolingReportComposable } from './types'
import { DomainDateUtils } from '@empathyco/playboard-core-domain'
import { getCurrentInstance, ref } from 'vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { CrudDataTypeEnum, SortDirectionEnum } from '../../enums'
import { CRUD_CONSTS } from '../../store/crud/crud.consts'
import { StringUtils } from '../../utils'
import { useFilters } from '../use-filters'
import { useToolingPagination } from '../use-tooling-pagination'
import { useToolingSorting } from '../use-tooling-sorting'
import { mapActions } from '../utils'

export function useToolingReportList(tool: FeatureIdEnum): ToolingReportComposable {
  const NUMBER_MAX_ITEMS = 200

  const ctx = getCurrentInstance()
  const { selectedDates } = useFilters()
  const {
    itemsPerPageOptions,
    pagination,
    setToolingPagination,
    resetPage,
    setToolingPaginationBasedOnSort,
  } = useToolingPagination()
  const { sort, setToolingSorting } = useToolingSorting()
  const loading = ref(false)

  const { listAction } = mapActions(ctx.proxy.$store, CRUD_CONSTS.moduleName, {
    listAction: CRUD_CONSTS.actions.list,
  })

  async function fetchPaginatedData(): Promise<ToolReportEntry[]> {
    loading.value = true
    pagination.value.totalItems = await getTotalItems()
    const service = CONTAINER.get<ToolReportingService>(TYPES.ToolReportingService)
    const response = await service.list(buildServiceListParams())
    loading.value = false
    return response
  }

  async function getTotalItems(): Promise<number> {
    const service = CONTAINER.get<ToolReportingService>(TYPES.ToolReportingService)
    const response = await service.listTotals(buildServiceListParams())
    return response.toolsCount
  }

  async function getNumberMaxPromotions(): Promise<number> {
    const response = await listAction({
      dataType: CrudDataTypeEnum.PROMOTION,
      pagination: pagination.value,
    })
    return response.data.total
  }

  function getItemsPerPage(totalItems: number): number {
    if (totalItems < NUMBER_MAX_ITEMS) {
      return totalItems
    }
    return NUMBER_MAX_ITEMS
  }

  async function getAllPromotions(totalItems: number): Promise<Promotion[]> {
    let allPromotions = []
    for (let i = 0; i < totalItems / NUMBER_MAX_ITEMS; i++) {
      const response = await listAction({
        dataType: CrudDataTypeEnum.PROMOTION,
        pagination: { page: i + 1, itemsPerPage: getItemsPerPage(totalItems) },
      })
      allPromotions = [...allPromotions, ...response.data.items]
    }
    return allPromotions
  }

  function createToolReportEntry(promotionWithNoReportEntry: Promotion[]): ToolReportEntry[] {
    return promotionWithNoReportEntry.map(promo => ({
      name: promo.title,
      id: promo.id,
      queries: promo.keywords,
      ctr: 0,
      averagePosition: 0,
      clickCount: 0,
    }))
  }

  function getPromotionsFilteredBySchedule(allPromotionsData: Promotion[]): Promotion[] {
    const [startDate, endDate] = selectedDates.value
    return allPromotionsData.filter(
      promo =>
        (!promo.schedule.activationDate && !promo.schedule.deactivationDate) ||
        DomainDateUtils.arePeriodsOverlapped(
          {
            start: promo.schedule?.activationDate,
            end: promo.schedule?.deactivationDate,
          },
          {
            start: new Date(startDate),
            end: new Date(endDate),
          },
        ),
    )
  }

  function getMergedData(
    toolReportingData: ToolReportEntry[],
    promotionsFilteredBySchedule: Promotion[],
  ): ToolReportEntry[] {
    const toolReportingDataIds = toolReportingData.map(report => report.id)
    return [
      ...sortToolReportEntries(toolReportingData),
      ...sortToolReportEntries(
        createToolReportEntry(
          promotionsFilteredBySchedule.filter(promo => !toolReportingDataIds.includes(promo.id)),
        ),
      ),
    ]
  }

  function sortToolReportEntries(toolReportEntries: ToolReportEntry[]): ToolReportEntry[] {
    return toolReportEntries.sort(
      (toolReportEntry1: ToolReportEntry, toolReportEntry2: ToolReportEntry) =>
        StringUtils.compareCaseInsensitive(toolReportEntry1.name, toolReportEntry2.name),
    )
  }

  async function getToolReportingData(): Promise<ToolReportEntry[]> {
    const service = CONTAINER.get<ToolReportingService>(TYPES.ToolReportingService)
    const { page, sort, ...serviceListParams } = buildServiceListParams()
    const response = await service.list(serviceListParams)
    return response
  }

  async function fetchData(): Promise<ToolReportEntry[]> {
    loading.value = true
    try {
      const maxPromotions = await getNumberMaxPromotions()
      const allPromotionsData = await getAllPromotions(maxPromotions)
      const promotionsFilteredBySchedule = getPromotionsFilteredBySchedule(allPromotionsData)
      const toolReportingData = await getToolReportingData()
      const toolReportingDataMerged = getMergedData(toolReportingData, promotionsFilteredBySchedule)
      return toolReportingDataMerged
    } catch (error) {
      ctx.proxy.$emit('showError', error)
    } finally {
      loading.value = false
    }
  }

  function buildServiceListParams(): ToolReportingListParams {
    const serviceListParams: ToolReportingListParams = {
      tool,
      ...buildServiceListPeriodParams(),
      page: buildServiceListPageParam(),
      sort: buildServiceListSortParam(),
    }

    return serviceListParams
  }

  function buildServiceListPeriodParams(): Pick<ToolReportingListParams, 'startDate' | 'endDate'> {
    const [startDate, endDate] = selectedDates.value
    return {
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
    }
  }

  function buildServiceListPageParam(): ToolReportingListParams['page'] {
    const pageStart = (pagination.value.page - 1) * pagination.value.itemsPerPage
    return {
      start: pageStart,
      rows: pagination.value.itemsPerPage,
    }
  }

  function buildServiceListSortParam(): ToolReportingListParams['sort'] {
    const sortField = sort.value?.field
    if (!sortField) {
      return undefined
    }

    if (!sort.value?.direction) {
      return undefined
    }

    const sortDirection = {
      [SortDirectionEnum.ASCENDING]: 'ASC',
      [SortDirectionEnum.DESCENDING]: 'DESC',
    }[sort.value.direction] as ToolReportingListParams['sort']['direction']

    return {
      field: sortField,
      direction: sortDirection,
    }
  }

  return {
    fetchData,
    fetchPaginatedData,
    getTotalItems,
    itemsPerPageOptions,
    pagination,
    resetPage,
    setToolingSorting,
    setToolingPagination,
    setToolingPaginationBasedOnSort,
    sort,
    loading,
  }
}
